import _extends from "@babel/runtime/helpers/extends";
import _get from "lodash/get";
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { display, fontFamily, fontSize, fontWeight, space, themeGet } from 'styled-system';
import { Button as BlueprintButton, Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import { formAriaProps, guessByChildText, guessTextOnIcon } from 'core/util/ariaUtils';
import { omitAriaProps } from 'core/form/components/util/omitFieldProps';
import Icon from './Icon';
import position from './utils/position';
import flex from './utils/flex';
import size from './utils/size';
const StyledButton = styled(BlueprintButton).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-177yb33-0"
})(["&&{&.", "{", ";", ";", ";", ";", ";", ";", ";", ";", " > .", "{overflow:hidden;flex:1 1 auto;text-align:left;color:", ";}&.align-center{> .", "{text-align:center;}}", "}}"], Classes.BUTTON, display, fontFamily, fontSize, fontWeight, space, position, flex, size, ({
  theme,
  small
}) => small && css(["font-size:", ";.bp4-icon{&:first-child{margin-right:4px;}&:first-child:last-child{margin:0 -7px;}}"], theme?.fontSizes.small), Classes.BUTTON_TEXT, ({
  color
}) => themeGet(`colors.${color}`, color), Classes.BUTTON_TEXT, props => props.ellipsis && css(["> .", "{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}"], Classes.BUTTON_TEXT));
const ButtonComponent = (props, ref) => {
  // destructure minWidth and alignSelf, otherwise we get non-standard prop warnings from React.
  const {
    active,
    activeBg,
    alignSelf,
    ariaLabel,
    ariaExpanded,
    ariaHaspopup = false,
    onSpaceOrEnter,
    border,
    minHeight,
    minWidth,
    maxWidth,
    maxHeight,
    borderRadius,
    className,
    color,
    icon,
    iconColor,
    rightIcon,
    large,
    loading,
    small,
    justifyContent,
    iconSize,
    width,
    height,
    textAlign = 'center',
    textColor,
    title,
    bg,
    flexDirection,
    alignItems,
    flexWrap,
    tracker,
    fontFamily: _fontFamily = 'body',
    // TODO these fields do nothing - are they missing functionality or are they safe to delete?
    shouldDismissPopover: _shouldDismissPopover,
    staticContext: _staticContext,
    ...rest
  } = props;
  const theme = useTheme();
  let finalIconSize = 16;
  if (iconSize) {
    finalIconSize = iconSize;
  } else if (large) {
    finalIconSize = 20;
  } else if (small) {
    finalIconSize = 12;
  }
  let background = bg;
  if (active && activeBg) {
    background = activeBg;
  }
  let ariaLabelGuess = rest.ellipsis ? rest.text : title;
  ariaLabelGuess = ariaLabelGuess || rest.text || guessByChildText(rest.children) || icon && guessTextOnIcon(icon) || rightIcon && guessTextOnIcon(rightIcon);
  const ariaLabelText = ariaLabel || ariaLabelGuess;
  const buttonClassName = classNames(className, tracker, {
    'align-center': textAlign === 'center'
  });
  const ariaProps = formAriaProps(props, {
    ariaLabel: ariaLabelText || rest.text,
    ariaPressed: active,
    ariaSelected: rest.ariaSelected || active,
    name: rest.name || rest.text || ariaLabel || rest['aria-label'],
    ariaHaspopup,
    ariaExpanded
  }, {
    enableTabIndexNotDisabled: true,
    explicitTabIndex: rest.explicitTabIndex || 0
  });
  if (rest.role) {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = rest.role;
  } else {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = 'button';
  }
  if (onSpaceOrEnter) {
    // @ts-expect-error this works but isn't really legal lol
    ariaProps.onKeyDown = event => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onSpaceOrEnter(event);
      }
    };
  }
  return /*#__PURE__*/React.createElement(StyledButton
  // rest has a tabIndex=undefined so it has to go first
  , _extends({}, omitAriaProps(rest), ariaProps, {
    active: active,
    large: large,
    loading: loading,
    small: small,
    className: buttonClassName,
    style: {
      alignSelf,
      alignItems,
      flexWrap,
      border,
      minHeight,
      minWidth,
      maxWidth,
      maxHeight,
      borderRadius,
      justifyContent,
      flexDirection,
      width,
      height,
      backgroundColor: background ? _get(theme?.colors, background) : undefined,
      color: textColor && _get(theme?.colors, textColor) || color && _get(theme?.colors, color) || undefined,
      ...(rest.style || {})
    },
    title: rest.ellipsis ? rest.text : title,
    icon: icon ? /*#__PURE__*/React.createElement(Icon, {
      icon: icon,
      color: iconColor || color,
      disabled: rest?.disabled,
      iconSize: finalIconSize
    }) : undefined,
    rightIcon: rightIcon ? /*#__PURE__*/React.createElement(Icon, {
      icon: rightIcon,
      color: iconColor || color,
      iconSize: finalIconSize
    }) : undefined,
    ref: ref
  }));
};
const Button = /*#__PURE__*/React.forwardRef(ButtonComponent);
Button.displayName = 'Button';
export default Button;